import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import HotelCard from "@/components/HotelCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, SlidersHorizontal, X, Users, ChevronDown, Star } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const STAR_CATEGORIES = ["3-star", "4-star", "5-star"];
const TYPE_CATEGORIES = ["boutique", "heritage", "business", "resort"];
const AMENITY_OPTIONS = [
  "Free WiFi", "Pool", "Spa", "Restaurant", "Free Parking",
  "AC", "Gym", "Bar", "Sea View", "Rooftop", "Business Center",
  "EV Charging", "Valet", "Concierge", "Banquet Hall", "Conference Room",
];

export default function HotelSearch() {
  const [params, setParams] = useSearchParams();
  const [hotels, setHotels] = useState([]);
  const [city, setCity] = useState(params.get("city") || "");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const activeCity = params.get("city");
  useSEO({
    title: activeCity ? `Hotels in ${activeCity} — Compare & Negotiate` : "Find your stay — SwaanStay",
    description: activeCity
      ? `Book hotels in ${activeCity} on SwaanStay. Compare prices, read verified reviews and negotiate directly with hotels to save up to 40%.`
      : "Search hotels across India. Filter by price, star rating & amenities. Negotiate any price directly with hotels.",
    keywords: activeCity ? `hotels in ${activeCity}, ${activeCity} hotels, cheap ${activeCity} hotels` : "hotel booking india, hotel search, cheap hotels",
    url: activeCity ? `https://price-compete.emergent.host/search?city=${encodeURIComponent(activeCity)}` : "https://price-compete.emergent.host/search",
  });

  // Guest selector
  const [adults, setAdults] = useState(Number(params.get("adults") || 2));
  const [children, setChildren] = useState(Number(params.get("children") || 0));
  const [roomsCount, setRoomsCount] = useState(Number(params.get("rooms") || 1));
  const [checkin, setCheckin] = useState(params.get("checkin") || "");
  const [checkout, setCheckout] = useState(params.get("checkout") || "");
  const [guestOpen, setGuestOpen] = useState(false);

  // Filters
  const [priceMax, setPriceMax] = useState(20000);
  const [minRating, setMinRating] = useState(0);
  const [selCategories, setSelCategories] = useState([]);
  const [selAmenities, setSelAmenities] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [similarPool, setSimilarPool] = useState([]);  // wider pool (all cities) for suggestions

  const fetchHotels = async (nextCity) => {
    setLoading(true);
    try {
      const res = await api.get("/hotels", { params: { city: (nextCity ?? city) || undefined, q: q || undefined } });
      setHotels(res.data);
    } finally {
      setLoading(false);
    }
  };

  // Broader pool (unfiltered by city) for the "You might also like" suggestions strip
  useEffect(() => {
    api.get("/hotels").then(({ data }) => setSimilarPool(data)).catch((e) => console.debug("fetch failed:", e?.message));
  }, []);

  useEffect(() => {
    fetchHotels(params.get("city") || "");
  }, [params]);

  const submit = (e) => {
    e.preventDefault();
    const next = {};
    if (city) next.city = city;
    if (checkin) next.checkin = checkin;
    if (checkout) next.checkout = checkout;
    if (adults !== 2) next.adults = String(adults);
    if (children !== 0) next.children = String(children);
    if (roomsCount !== 1) next.rooms = String(roomsCount);
    setParams(next);
    fetchHotels(city);
  };

  const toggle = (list, setList, val) =>
    setList(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const filtered = useMemo(() => {
    return hotels.filter((h) => {
      if ((h.base_price || 0) > priceMax) return false;
      if ((h.rating || 0) < minRating) return false;
      if (selCategories.length && !selCategories.includes((h.category || "").toLowerCase())) return false;
      if (selAmenities.length) {
        const amen = (h.amenities || []).map((a) => a);
        if (!selAmenities.every((a) => amen.includes(a))) return false;
      }
      return true;
    });
  }, [hotels, priceMax, minRating, selCategories, selAmenities]);

  const activeCount = (selCategories.length + selAmenities.length + (minRating ? 1 : 0) + (priceMax < 20000 ? 1 : 0));
  const guestSummary = `${adults + children} guest${adults + children > 1 ? "s" : ""} · ${roomsCount} room${roomsCount > 1 ? "s" : ""}`;

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          <div className="ss-label text-[#A6852F]">Browse</div>
          <h1 className="font-head text-4xl md:text-5xl font-black text-slate-900 mt-3">Find your <span className="ss-underline-gold text-[#A6852F]">stay</span></h1>
          <p className="text-slate-600 mt-2 font-body">Search by city, pick your dates and guests. Negotiate directly on any listing.</p>

          <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3 max-w-5xl">
            <div className="md:col-span-4">
              <Label className="hw-label text-slate-500">City / Hotel</Label>
              <Input data-testid="search-city-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Mumbai" className="rounded-sm h-11 border-slate-300 mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="hw-label text-slate-500">Check-in</Label>
              <Input data-testid="search-checkin-input" type="date" min={new Date().toISOString().slice(0,10)} value={checkin} onChange={(e) => setCheckin(e.target.value)} className="rounded-sm h-11 border-slate-300 mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="hw-label text-slate-500">Check-out</Label>
              <Input data-testid="search-checkout-input" type="date" min={checkin || new Date().toISOString().slice(0,10)} value={checkout} onChange={(e) => setCheckout(e.target.value)} className="rounded-sm h-11 border-slate-300 mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label className="hw-label text-slate-500">Guests & Rooms</Label>
              <Popover open={guestOpen} onOpenChange={setGuestOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" data-testid="guest-selector-btn" className="rounded-sm h-11 border-slate-300 justify-between w-full mt-1 font-normal">
                    <span className="flex items-center gap-2 text-slate-900"><Users className="w-4 h-4" /> {guestSummary}</span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 rounded-sm p-4" align="end">
                  {[
                    { label: "Adults", val: adults, set: setAdults, min: 1, max: 12 },
                    { label: "Children", val: children, set: setChildren, min: 0, max: 8 },
                    { label: "Rooms", val: roomsCount, set: setRoomsCount, min: 1, max: 8 },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between py-2" data-testid={`guest-row-${row.label.toLowerCase()}`}>
                      <div className="text-slate-800 font-medium">{row.label}</div>
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={() => row.set(Math.max(row.min, row.val - 1))} className="w-8 h-8 border border-slate-300 rounded-sm text-lg font-bold hover:bg-slate-100" data-testid={`${row.label.toLowerCase()}-minus`}>−</button>
                        <span className="w-6 text-center font-bold" data-testid={`${row.label.toLowerCase()}-count`}>{row.val}</span>
                        <button type="button" onClick={() => row.set(Math.min(row.max, row.val + 1))} className="w-8 h-8 border border-slate-300 rounded-sm text-lg font-bold hover:bg-slate-100" data-testid={`${row.label.toLowerCase()}-plus`}>+</button>
                      </div>
                    </div>
                  ))}
                  <Button type="button" onClick={() => setGuestOpen(false)} className="w-full mt-3 bg-slate-900 hover:bg-slate-800 text-white rounded-sm">Done</Button>
                </PopoverContent>
              </Popover>
            </div>
            <div className="md:col-span-2 flex items-end">
              <Button type="submit" data-testid="search-submit-btn" className="ss-btn ss-btn-primary rounded-xl h-11 w-full">
                <Search className="w-4 h-4 mr-2" /> Search
              </Button>
            </div>
          </form>

          <div className="mt-5 max-w-5xl bg-gradient-to-r from-[#FBF3DA] to-[#F7EAC4] border border-[#C8A34D]/40 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="group-booking-cta">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 ss-gold-gradient rounded-xl grid place-items-center text-slate-900 text-lg font-black">5+</div>
              <div>
                <div className="font-head font-bold text-slate-900">Booking 5+ rooms?</div>
                <div className="text-sm text-slate-600 font-body">Post one request — multiple hotels compete with AI-vetted quotes.</div>
              </div>
            </div>
            <Link to="/requests" data-testid="group-booking-link" className="ss-btn ss-btn-primary text-sm px-5 py-2.5">
              Post Group Booking Bid →
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid lg:grid-cols-12 gap-8">
        {/* Filters sidebar */}
        <aside className={`${showFilters ? "block" : "hidden"} lg:block lg:col-span-3`} data-testid="filters-sidebar">
          <div className="hw-card p-5 sticky top-20">
            <div className="flex items-center justify-between">
              <div className="hw-head font-black text-slate-900 flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Filters</div>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={() => { setPriceMax(20000); setMinRating(0); setSelCategories([]); setSelAmenities([]); }}
                  className="text-xs text-blue-700 font-bold hover:underline"
                  data-testid="clear-filters-btn"
                >Clear all</button>
              )}
            </div>

            <div className="mt-5">
              <Label className="hw-label text-slate-500">Max price / night</Label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs font-bold text-slate-700">₹1k</span>
                <input
                  type="range" min={1000} max={20000} step={500}
                  value={priceMax} onChange={(e) => setPriceMax(Number(e.target.value))}
                  className="flex-1 accent-blue-700"
                  data-testid="price-range-slider"
                />
                <span className="text-xs font-bold text-slate-700">₹20k+</span>
              </div>
              <div className="mt-2 text-sm font-bold text-blue-700" data-testid="price-max-label">Up to ₹{priceMax.toLocaleString("en-IN")}</div>
            </div>

            <div className="mt-6">
              <Label className="hw-label text-slate-500">Minimum rating</Label>
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {[0, 3, 3.5, 4, 4.5].map((r) => (
                  <button
                    key={r} type="button"
                    onClick={() => setMinRating(r)}
                    className={`px-2.5 py-1.5 border rounded-sm text-xs font-bold flex items-center gap-1 transition ${minRating === r ? "border-blue-700 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700 hover:border-slate-500"}`}
                    data-testid={`rating-filter-${r}`}
                  >
                    {r === 0 ? "Any" : <><Star className="w-3 h-3 fill-amber-500 stroke-amber-500" /> {r}+</>}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <Label className="hw-label text-slate-500">Star category</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {STAR_CATEGORIES.map((c) => (
                  <button
                    key={c} type="button"
                    onClick={() => toggle(selCategories, setSelCategories, c)}
                    className={`px-2.5 py-1 border rounded-sm text-xs font-bold ${selCategories.includes(c) ? "border-blue-700 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700 hover:border-slate-500"}`}
                    data-testid={`cat-${c}`}
                  >{c}</button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <Label className="hw-label text-slate-500">Property type</Label>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {TYPE_CATEGORIES.map((c) => (
                  <button
                    key={c} type="button"
                    onClick={() => toggle(selCategories, setSelCategories, c)}
                    className={`px-2.5 py-1 border rounded-sm text-xs font-bold capitalize ${selCategories.includes(c) ? "border-blue-700 bg-blue-50 text-blue-700" : "border-slate-300 text-slate-700 hover:border-slate-500"}`}
                    data-testid={`cat-${c}`}
                  >{c}</button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <Label className="hw-label text-slate-500">Amenities</Label>
              <div className="mt-2 space-y-2 max-h-64 overflow-y-auto pr-1">
                {AMENITY_OPTIONS.map((a) => (
                  <label key={a} className="flex items-center gap-2 cursor-pointer" data-testid={`amenity-${a.replace(/\s+/g, '-').toLowerCase()}`}>
                    <Checkbox checked={selAmenities.includes(a)} onCheckedChange={() => toggle(selAmenities, setSelAmenities, a)} className="rounded-sm" />
                    <span className="text-sm text-slate-700">{a}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-9">
          <div className="flex items-center justify-between mb-4">
            <div className="hw-label text-slate-500" data-testid="results-count">{loading ? "Loading…" : `${filtered.length} of ${hotels.length} hotels`}</div>
            <button
              type="button"
              onClick={() => setShowFilters((s) => !s)}
              className="lg:hidden flex items-center gap-2 text-sm font-bold text-blue-700 border border-blue-700 rounded-sm px-3 py-1.5"
              data-testid="mobile-filter-toggle"
            >
              {showFilters ? <X className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
              {showFilters ? "Close" : "Filters"} {activeCount > 0 && <span className="bg-blue-700 text-white rounded-full px-1.5 text-[10px]">{activeCount}</span>}
            </button>
          </div>

          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="search-results">
            {filtered.map((h) => <HotelCard key={h.hotel_id} hotel={h} />)}
          </div>
          {!loading && filtered.length === 0 && (
            <div className="hw-card p-10 text-center mt-5" data-testid="no-results">
              <p className="text-slate-600">No hotels match your filters — try clearing some or changing the city.</p>
            </div>
          )}

          {/* Similar-category suggestions — helps travellers discover more options as they scroll */}
          {!loading && filtered.length > 0 && (() => {
            // Normalise city aliases (Bangalore ≡ Bengaluru, Bombay ≡ Mumbai etc.) so
            // same-metro hotels don't sneak into the "you might also like" strip.
            const CITY_ALIASES = {
              "bangalore": "bengaluru", "bengaluru": "bengaluru",
              "bombay": "mumbai", "mumbai": "mumbai",
              "calcutta": "kolkata", "kolkata": "kolkata",
              "madras": "chennai", "chennai": "chennai",
              "gurgaon": "gurugram", "gurugram": "gurugram",
            };
            const normCity = (c) => CITY_ALIASES[(c || "").toLowerCase()] || (c || "").toLowerCase();
            const shownIds = new Set(filtered.map((h) => h.hotel_id));
            const shownCities = new Set(filtered.map((h) => normCity(h.city)));
            const preferredCats = selCategories.length > 0
              ? selCategories.map((c) => c.toLowerCase())
              : Array.from(new Set(filtered.slice(0, 6).map((h) => (h.category || "").toLowerCase()).filter(Boolean)));
            const pool = similarPool.length > 0 ? similarPool : hotels;
            let similar = pool
              .filter((h) => !shownIds.has(h.hotel_id))
              .filter((h) => !shownCities.has(normCity(h.city)))
              .filter((h) => !preferredCats.length || preferredCats.includes((h.category || "").toLowerCase()));
            // If no category match after city dedup, drop the category filter
            if (similar.length === 0) {
              similar = pool.filter((h) => !shownIds.has(h.hotel_id) && !shownCities.has(normCity(h.city)));
            }
            similar = similar.sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 6);
            if (similar.length === 0) return null;
            return (
              <section className="mt-14" data-testid="similar-hotels">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="hw-label text-blue-700">You might also like</div>
                    <h2 className="hw-head text-2xl md:text-3xl font-black text-slate-900 mt-2">Similar hotels {city ? `beyond ${city}` : ""}</h2>
                    <p className="text-slate-600 text-sm mt-1">Hand-picked stays with a similar category and rating.</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
                  {similar.map((h) => <HotelCard key={h.hotel_id} hotel={h} />)}
                </div>
              </section>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
